import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import { ConfirmAction } from '@/components/confirm-action'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  ALLOWED_CONDITIONS,
  CONDITION_TYPES,
  COMPLETION_RULES,
  LIVE_BADGE,
  LIVE_LABEL,
  REWARD_RELEASES,
  fromLocalInput,
  questLiveState,
  questSchema,
  sumCredits,
  toLocalInput,
  useQuest,
  useSaveQuest,
  useSetQuestEnd,
  utcLabel,
  type ConditionType,
  type QuestDetail,
  type QuestInput,
  type TaskInput,
} from '@/features/quests/api'

/**
 * `staff_manual` defaults on a fresh sub-task: it is the only condition that
 * works for a task nobody has written an evaluator for, which is what an
 * operator authoring something new almost always has.
 */
function blankTask(sort_order: number): TaskInput {
  return {
    title: '',
    sort_order,
    required: false,
    internal_credit: 0,
    conditions: [{ type: 'staff_manual' }],
  }
}

function toFormValues(quest?: QuestDetail): QuestInput {
  if (!quest) {
    return {
      name: '',
      description: '',
      status: 'draft',
      reward_release: 'require_approval',
      completion_rule: 'ALL',
      starts_at: null,
      ends_at: null,
      tasks: [blankTask(0)],
    }
  }
  return {
    name: quest.name,
    description: quest.description ?? '',
    status: quest.status,
    reward_release: quest.reward_release,
    completion_rule: quest.completion_rule,
    threshold: quest.threshold,
    starts_at: quest.starts_at,
    ends_at: quest.ends_at,
    // Copied rather than referenced — the field array mutates what it is given,
    // and that object is React Query's cached response.
    tasks: quest.tasks.map((task) => ({
      ...task,
      conditions: task.conditions.map((condition) => ({ type: condition.type })),
    })),
  }
}

interface QuestEditorProps {
  /** Absent for a new quest. */
  id?: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Fetches the quest, then builds the form from it. The two halves are separate
 * components on purpose: `useForm` captures its defaults on the render it first
 * runs on, so a form mounted before the fetch resolves would have to be seeded
 * afterwards in an effect — which paints empty inputs first and then overwrites
 * anything typed into them.
 */
export function QuestEditor({ id, open, onOpenChange }: QuestEditorProps) {
  const detail = useQuest(id)
  const loading = id !== undefined && detail.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        {loading ? (
          <>
            <DialogHeader>
              <DialogTitle>Loading quest…</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </>
        ) : detail.isError ? (
          <>
            <DialogHeader>
              <DialogTitle>Could not load this quest</DialogTitle>
              <DialogDescription>{(detail.error as Error).message}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => void detail.refetch()}>
                Try again
              </Button>
            </DialogFooter>
          </>
        ) : (
          <QuestForm quest={detail.data} onDone={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function QuestForm({ quest, onDone }: { quest?: QuestDetail; onDone: () => void }) {
  const save = useSaveQuest()
  const setEnd = useSetQuestEnd()

  /**
   * Published quests are read-only apart from `ends_at`. Every other input is
   * disabled rather than hidden, so an operator can still read what the quest
   * actually promises while they decide whether to end it.
   */
  const locked = quest?.status === 'active'
  const liveState = quest ? questLiveState(quest) : 'draft'

  const form = useForm<QuestInput>({
    resolver: standardSchemaResolver(questSchema),
    defaultValues: toFormValues(quest),
  })
  const tasks = useFieldArray({ control: form.control, name: 'tasks' })

  // Validated values parked here while the publish confirmation is open, so the
  // quest is never checked *after* the operator has agreed to publish it.
  const [pendingPublish, setPendingPublish] = useState<QuestInput | null>(null)
  const [endingNow, setEndingNow] = useState(false)

  const watchedTasks = form.watch('tasks') ?? []
  const rule = form.watch('completion_rule')
  const startsAt = form.watch('starts_at')
  const endsAt = form.watch('ends_at')

  const derivedPoints = sumCredits(watchedTasks)
  const optionalCount = watchedTasks.filter((task) => !task.required).length
  const manualCount = watchedTasks.filter(
    (task) => task.conditions[0]?.type === 'staff_manual',
  ).length

  const errors = form.formState.errors
  const mutationError = (save.error ?? setEnd.error) as Error | undefined

  function setWindow(field: 'starts_at' | 'ends_at', value: string) {
    form.setValue(field, fromLocalInput(value), { shouldDirty: true, shouldValidate: true })
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {/* A new quest has no id yet, so the chip is absent rather than blank.
              font-normal because DialogTitle is semibold and the chip would
              otherwise compete with the name it labels. */}
          {quest && (
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
              {quest.id}
            </code>
          )}
          {quest ? quest.name : 'New quest'}
          {quest && <Badge variant={LIVE_BADGE[liveState]}>{LIVE_LABEL[liveState]}</Badge>}
        </DialogTitle>
        <DialogDescription>
          {locked ? (
            <>
              <b>Published quests are immutable.</b> Only the end date can still change — fix a
              mistake by ending this quest and publishing a replacement, never by editing it under
              members who are part-way through.
            </>
          ) : (
            <>
              Sub-tasks, one completion condition each, and a staff-only credit per task. The
              reward is the sum of those credits and <b>freezes when you publish</b>.
            </>
          )}
        </DialogDescription>
      </DialogHeader>

      <form
        id="quest-form"
        className="space-y-5"
        onSubmit={form.handleSubmit((values) =>
          locked
            ? setEnd.mutate({ id: quest!.id, ends_at: values.ends_at }, { onSuccess: onDone })
            : save.mutate(
                { id: quest?.id, input: { ...values, status: 'draft' } },
                { onSuccess: onDone },
              ),
        )}
      >
        <div className="space-y-2">
          <Label htmlFor="quest-name">Quest name</Label>
          <Input id="quest-name" disabled={locked} {...form.register('name')} />
          {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="quest-description">Description</Label>
          <Textarea id="quest-description" rows={2} disabled={locked} {...form.register('description')} />
          {errors.description && (
            <p className="text-xs text-destructive">{errors.description.message}</p>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="quest-release">Reward release</Label>
            <Select
              disabled={locked}
              value={form.watch('reward_release')}
              onValueChange={(value) =>
                form.setValue('reward_release', value as QuestInput['reward_release'], {
                  shouldDirty: true,
                })
              }
            >
              <SelectTrigger id="quest-release">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REWARD_RELEASES.map(({ code, label }) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-faint">
              {form.watch('reward_release') === 'require_approval'
                ? 'Completions land in the reward approval queue before points are credited.'
                : 'Points are credited on completion — the approval queue is skipped entirely.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label>Reward</Label>
            <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-secondary px-3 text-sm">
              <b>{locked ? quest!.reward_points : derivedPoints}</b>
              <span className="text-muted-foreground">points</span>
            </div>
            <p className="text-xs text-faint">
              {locked
                ? 'Frozen at publish. It can no longer follow the task credits.'
                : 'Derived from the sum of task credits. Not editable — an editable copy of a derived number is how a total drifts away from the tasks it came from.'}
            </p>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="quest-rule">Completion rule</Label>
            <Select
              disabled={locked}
              value={rule}
              onValueChange={(value) => {
                form.setValue('completion_rule', value as QuestInput['completion_rule'], {
                  shouldDirty: true,
                  shouldValidate: true,
                })
                // Cleared on the way to ALL, not just on the way out in
                // `normalise`. A half-typed threshold left behind would fail
                // validation while its input is unmounted, so the submit would
                // do nothing with no error anywhere on screen.
                if (value === 'ALL') form.setValue('threshold', undefined)
              }}
            >
              <SelectTrigger id="quest-rule">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COMPLETION_RULES.map(({ code, label }) => (
                  <SelectItem key={code} value={code}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.completion_rule && (
              <p className="text-xs text-destructive">{errors.completion_rule.message}</p>
            )}
          </div>

          {/* Only meaningful under N_OF, and the backend's check constraint
              rejects a threshold on an ALL quest — so it is not merely hidden,
              it is cleared on the way out (see normalise). */}
          {rule === 'N_OF' && (
            <div className="space-y-2">
              <Label htmlFor="quest-threshold">Complete how many? (N)</Label>
              <Input
                id="quest-threshold"
                type="number"
                min={1}
                max={Math.max(1, optionalCount)}
                disabled={locked}
                {...form.register('threshold')}
              />
              <p className="text-xs text-faint">
                Counted against the {optionalCount} optional sub-task
                {optionalCount === 1 ? '' : 's'}. Sub-tasks marked <b>Required</b> sit outside that
                pool and must always be done.
              </p>
              {errors.threshold && (
                <p className="text-xs text-destructive">{errors.threshold.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="quest-starts">Starts</Label>
            <Input
              id="quest-starts"
              type="datetime-local"
              disabled={locked}
              value={toLocalInput(startsAt)}
              onChange={(event) => setWindow('starts_at', event.target.value)}
            />
            <p className="text-xs text-faint">
              {startsAt ? utcLabel(startsAt) : 'Empty — live the moment it is published.'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="quest-ends">Ends</Label>
            <Input
              id="quest-ends"
              type="datetime-local"
              value={toLocalInput(endsAt)}
              onChange={(event) => setWindow('ends_at', event.target.value)}
            />
            <p className="text-xs text-faint">
              {endsAt ? utcLabel(endsAt) : 'Empty — no expiry.'}
            </p>
            {errors.ends_at && <p className="text-xs text-destructive">{errors.ends_at.message}</p>}
          </div>
        </div>
        {/* Times are entered in this browser's timezone; the UTC equivalent under
            each field is what is actually stored and compared. */}
        <p className="text-xs text-faint">
          Entered in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}). A
          qualifying action only counts inside this window — nothing done before the start
          completes a task.
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>
              Sub-tasks{' '}
              <span className="font-normal text-faint">
                — order shown to members is the order here
              </span>
            </Label>
            {!locked && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => tasks.append(blankTask(tasks.fields.length))}
              >
                <Plus />
                Add sub-task
              </Button>
            )}
          </div>

          {tasks.fields.map((field, index) => {
            const taskErrors = errors.tasks?.[index]
            const conditionType = watchedTasks[index]?.conditions?.[0]?.type
            const meta = CONDITION_TYPES.find((candidate) => candidate.type === conditionType)

            return (
              <div key={field.id} className="space-y-3 rounded-lg border border-input p-3">
                <div className="flex items-center gap-2">
                  <Badge variant="accent">{index + 1}</Badge>
                  <Input
                    placeholder="Sub-task title, e.g. Join the Discord server"
                    aria-label={`Sub-task ${index + 1} title`}
                    disabled={locked}
                    {...form.register(`tasks.${index}.title` as const)}
                  />
                  {!locked && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Move up"
                        disabled={index === 0}
                        onClick={() => tasks.swap(index, index - 1)}
                      >
                        <ChevronUp />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Move down"
                        disabled={index === tasks.fields.length - 1}
                        onClick={() => tasks.swap(index, index + 1)}
                      >
                        <ChevronDown />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove sub-task"
                        disabled={tasks.fields.length === 1}
                        onClick={() => tasks.remove(index)}
                      >
                        <Trash2 />
                      </Button>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-56 flex-1 space-y-1.5">
                    <Label className="text-xs" htmlFor={`task-${index}-condition`}>
                      Completed when
                    </Label>
                    <Select
                      disabled={locked}
                      value={conditionType}
                      onValueChange={(value) =>
                        form.setValue(
                          `tasks.${index}.conditions.0.type` as const,
                          value as ConditionType,
                          { shouldDirty: true },
                        )
                      }
                    >
                      <SelectTrigger id={`task-${index}-condition`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {/* `follow_x` is in CONDITION_TYPES for the label table but
                            is not offered: validation rejects it in Phase 1, so a
                            selectable option could only produce a 1001. */}
                        {CONDITION_TYPES.filter((candidate) =>
                          (ALLOWED_CONDITIONS as readonly string[]).includes(candidate.type),
                        ).map(({ type, label }) => (
                          <SelectItem key={type} value={type}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-28 space-y-1.5">
                    <Label className="text-xs" htmlFor={`task-${index}-credit`}>
                      Credit
                    </Label>
                    <Input
                      id={`task-${index}-credit`}
                      type="number"
                      min={0}
                      disabled={locked}
                      {...form.register(`tasks.${index}.internal_credit` as const)}
                    />
                  </div>

                  {/* Under ALL every task is required by definition, so the toggle
                      would be a control with no effect. */}
                  {rule === 'N_OF' && (
                    <div className="flex items-center gap-2 pb-2">
                      <Switch
                        id={`task-${index}-required`}
                        disabled={locked}
                        checked={Boolean(watchedTasks[index]?.required)}
                        onCheckedChange={(checked) =>
                          form.setValue(`tasks.${index}.required` as const, checked, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                      />
                      <Label className="text-xs" htmlFor={`task-${index}-required`}>
                        Required
                      </Label>
                    </div>
                  )}
                </div>

                {meta && (
                  <p className="text-xs text-faint">
                    {meta.kind === 'manual' && <b>Staff-verified · </b>}
                    {meta.hint}
                  </p>
                )}
                {(taskErrors?.title || taskErrors?.internal_credit) && (
                  <p className="text-xs text-destructive">
                    {taskErrors.title?.message ?? taskErrors.internal_credit?.message}
                  </p>
                )}
              </div>
            )
          })}

          {(errors.tasks?.root?.message ?? errors.tasks?.message) && (
            <p className="text-xs text-destructive">
              {errors.tasks.root?.message ?? errors.tasks.message}
            </p>
          )}
          <p className="text-xs text-faint">
            {manualCount === 0
              ? 'Every sub-task here is evaluated automatically — nothing will reach the stage ① verification queue.'
              : `${manualCount} sub-task${manualCount === 1 ? ' needs' : 's need'} staff verification and will appear in the Rewards stage ① queue.`}
          </p>
        </div>

        {mutationError && <p className="text-xs text-destructive">{mutationError.message}</p>}
      </form>

      <DialogFooter>
        <Button type="button" variant="secondary" onClick={onDone}>
          {locked ? 'Close' : 'Cancel'}
        </Button>

        {locked ? (
          <>
            {/* Offered only while the quest is actually live. A scheduled quest
                cannot be ended now — `ends_at` may not precede `starts_at` — and
                the schema has no way to express "cancelled before it ran". */}
            {liveState === 'live' && (
              <Button type="button" variant="destructive" onClick={() => setEndingNow(true)}>
                End now
              </Button>
            )}
            <Button type="submit" form="quest-form" disabled={setEnd.isPending}>
              {setEnd.isPending ? 'Saving…' : 'Update end date'}
            </Button>
          </>
        ) : (
          <>
            <Button type="submit" form="quest-form" variant="secondary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save draft'}
            </Button>
            <Button
              type="button"
              disabled={save.isPending}
              onClick={form.handleSubmit((values) => setPendingPublish(values))}
            >
              Publish
            </Button>
          </>
        )}
      </DialogFooter>

      <ConfirmAction
        open={pendingPublish !== null}
        onOpenChange={(open) => !open && setPendingPublish(null)}
        title="Publish this quest?"
        description={
          <>
            Every member is enrolled the moment it goes live, the reward freezes at{' '}
            <b>{derivedPoints} points</b>, and the quest becomes <b>immutable</b> — after this only
            the end date can change. There is no un-publishing.
          </>
        }
        confirmLabel="Publish"
        onConfirm={() =>
          save.mutateAsync(
            { id: quest?.id, input: { ...pendingPublish!, status: 'active' } },
            { onSuccess: onDone },
          )
        }
      />

      <ConfirmAction
        open={endingNow}
        onOpenChange={setEndingNow}
        title="End this quest now?"
        description={
          <>
            It closes to new completions immediately. Members already awaiting a reward finished
            while it was live, so they stay in the approval queue and remain payable — only members
            still in progress lose the chance to finish.
          </>
        }
        confirmLabel="End now"
        destructive
        onConfirm={() =>
          setEnd.mutateAsync(
            { id: quest!.id, ends_at: new Date().toISOString() },
            { onSuccess: onDone },
          )
        }
      />
    </>
  )
}
